/**
 * Persistência local do roteiro.
 *
 * Duas coisas precisam sobreviver a fechar e reabrir a PWA:
 *
 *   1. o **estado otimista** — a alteração feita offline continua visível;
 *   2. o **outbox** — a mutação continua pendente e será enviada.
 *
 * Salvar só o primeiro faria a alteração parecer aplicada e nunca chegar ao
 * servidor. Salvar só o segundo faria a alteração sumir da tela até
 * sincronizar. Os dois, sempre, na mesma escrita.
 */

import type { Activity } from '@nv/core';
import { type LocalDatabase, openLocalDatabase, requestPersistentStorage } from '@nv/offline';

import type { HistoryEntry } from './store';

/**
 * O estado do roteiro é guardado **por dia**.
 *
 * Uma chave única funcionava enquanto existia uma viagem só. Com várias
 * viagens, o cache de um dia seria lido como se fosse o de outro — e o roteiro
 * de uma viagem apareceria dentro da outra. É o mesmo cuidado que `loadTrip`
 * já tinha ao comparar o `tripId`.
 */
const chaveDoEstado = (dayId: string) => `itinerary:state:${dayId}`;
const CHAVE_CURSOR = 'itinerary:cursor';
const CHAVE_VIAGEM = 'trip:working-set';

export interface PersistedItinerary {
  activities: Activity[];
  history: HistoryEntry[];
  currentMemberId: string;
  /** Versão do conjunto ordenado, para o conflito de reordenação. */
  reorderVersion: number;
  dayId: string;
}

let bancoLocal: LocalDatabase | null = null;

export async function localDb(): Promise<LocalDatabase> {
  if (!bancoLocal) {
    bancoLocal = await openLocalDatabase();
    // Reduz muito a chance de o Safari descartar os dados de uma PWA
    // instalada. Não é garantia; o servidor continua sendo a verdade.
    void requestPersistentStorage();
  }
  return bancoLocal;
}

export async function loadItinerary(dayId: string): Promise<PersistedItinerary | null> {
  if (!dayId) return null;
  const db = await localDb();
  const guardado = await db.cache.get<PersistedItinerary>(chaveDoEstado(dayId));
  // Cache de outro dia não serve — e servir seria mostrar o dia errado.
  return guardado?.dayId === dayId ? guardado : null;
}

export async function saveItinerary(state: PersistedItinerary): Promise<void> {
  if (!state.dayId) return;
  const db = await localDb();
  await db.cache.set(chaveDoEstado(state.dayId), state);
}

export async function loadCursor(): Promise<number> {
  const db = await localDb();
  return (await db.cache.get<number>(CHAVE_CURSOR)) ?? 0;
}

export async function saveCursor(cursor: number): Promise<void> {
  const db = await localDb();
  await db.cache.set(CHAVE_CURSOR, cursor);
}

/**
 * Working set da viagem ativa: quem sou eu nela, o que posso e quem mais está.
 *
 * Precisa sobreviver a abrir o app sem rede — sem isto, registrar uma despesa
 * offline seria impossível, porque a tela não saberia entre quem dividir. É o
 * mesmo motivo de o roteiro persistir o estado: o app tem de funcionar num
 * parque com sinal ruim, não só com Wi-Fi.
 */
export interface PersistedTrip {
  tripId: string;
  meuMemberId: string;
  meuUserId: string;
  permissions: string[];
  members: { memberId: string; userId: string; name: string; role: string }[];
}

export async function loadTrip(tripId: string): Promise<PersistedTrip | null> {
  const db = await localDb();
  const guardado = await db.cache.get<PersistedTrip>(CHAVE_VIAGEM);
  // Cache de outra viagem não serve — e servir seria mostrar gente errada.
  return guardado?.tripId === tripId ? guardado : null;
}

export async function saveTrip(data: PersistedTrip): Promise<void> {
  const db = await localDb();
  await db.cache.set(CHAVE_VIAGEM, data);
}

/** Apaga o cache do roteiro. Usado no logout e na saída da viagem. */
export async function clearItinerary(): Promise<void> {
  const db = await localDb();
  await db.cache.clear();
}
